import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div
        data-testid="contact-success"
        className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center"
      >
        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-emerald-800 mb-2">
          Message Sent!
        </h3>
        <p className="text-emerald-700">
          Thank you for contacting Solid Repair LLC. We'll get back to you soon!
        </p>
        <Button
          data-testid="send-another-btn"
          onClick={() => setSubmitStatus(null)}
          className="mt-6 bg-emerald-600 hover:bg-emerald-700"
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-slate-700 font-medium">
            Your Name
          </Label>
          <Input
            id="name"
            name="name"
            data-testid="contact-name-input"
            value={formData.name}
            onChange={handleChange}
            required
            minLength={2}
            placeholder="John Doe"
            className="border-slate-300 focus:border-amber-500 focus:ring-amber-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700 font-medium">
            Email Address
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            data-testid="contact-email-input"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="john@example.com"
            className="border-slate-300 focus:border-amber-500 focus:ring-amber-500"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-slate-700 font-medium">
          Phone Number
        </Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          data-testid="contact-phone-input"
          value={formData.phone}
          onChange={handleChange}
          required
          minLength={10}
          placeholder="(904) 555-0123"
          className="border-slate-300 focus:border-amber-500 focus:ring-amber-500"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="message" className="text-slate-700 font-medium">
          How Can We Help?
        </Label>
        <Textarea
          id="message"
          name="message"
          data-testid="contact-message-input"
          value={formData.message}
          onChange={handleChange}
          required
          minLength={10}
          rows={5}
          placeholder="Describe your electrical needs..."
          className="border-slate-300 focus:border-amber-500 focus:ring-amber-500 resize-none"
        />
      </div>

      {submitStatus === 'error' && (
        <div
          data-testid="contact-error"
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
        >
          Something went wrong. Please try again or call us directly.
        </div>
      )}

      <Button
        type="submit"
        data-testid="contact-submit-btn"
        disabled={isSubmitting}
        className="w-full bg-amber-600 hover:bg-amber-500 text-white py-3 font-semibold text-base transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/25"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};
